// AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 · spec 002 C1
// The shared guard for AGE's bubbling `mudClose`.
//
// Seven MUD components emit `mudClose` (`mud-modal`, `mud-select`,
// `mud-toast`, `mud-info-box`, `mud-menu`, `mud-phone-input`,
// `mud-tooltip`), and Stencil's `@Event()` defaults to `bubbles + composed`.
// A `<MudModal onMudClose>` therefore also fires when any of the other six
// closes *inside* the modal: choosing an option in a select, or dismissing a
// notification, tears the modal down under the user. This is not a select
// defect and not a two-screen defect — five screens break today and nineteen
// more are one `closable` attribute away.
//
// Nothing is fixed upstream: the vendored tarball, the local checkout's HEAD
// and `origin/main` are all 7322a157, so there is nothing newer to vendor. The
// day AGE stops bubbling this event, `grep -rn 'AGE-BUG(mudClose-bubbles)'`
// finds this helper and every one of its call sites, and all of it can be
// deleted.

/**
 * The `reason` AGE's modal reports for a dismissal.
 *
 * Mirrored rather than imported: this library's module path exposes only
 * `@egov-moldova/mud-react` (tsconfig.base.json), not the core package that
 * declares `MODAL_CLOSE_REASONS`. `on-modal-dismiss.spec.ts` asserts at compile
 * time that this union is still exactly AGE's, so the mirror cannot drift in
 * silence.
 */
export type ModalCloseReason = 'backdrop' | 'escape' | 'close-button' | 'action';

/**
 * `mudClose` as it arrives at a modal's handler — from the modal (`{reason}`)
 * or from a descendant that emitted its own (`null`, or a tooltip's `{reason}`).
 */
export type MudCloseEvent = CustomEvent<{ reason?: string } | null>;

/**
 * Wraps a `<MudModal onMudClose>` handler so it runs for a genuine dismissal
 * and stays silent for an event that merely bubbled through the modal.
 *
 * @example
 * ```tsx
 * // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 —
 * // seven AGE components emit `mudClose` as a bubbling, composed event, so a
 * // descendant closing inside this modal reaches this handler too.
 * <MudModal onMudClose={onModalDismiss(() => setOpen(false))}>
 * ```
 *
 * Two tests, in this order:
 *
 * 1. `ev.target === ev.currentTarget` — the primary one. A composed event is
 *    retargeted to the host element it escaped from, so a descendant's own
 *    `mudClose` never names the modal as its target.
 * 2. `typeof ev.detail?.reason === 'string'` — the secondary one, and **not
 *    sufficient alone**: `mud-tooltip` emits a `reason` too, and its union
 *    (`blur | escape | close-button | click-outside | click-trigger`) overlaps
 *    the modal's on `escape` and `close-button`. A tooltip inside a modal would
 *    defeat a reason-only guard.
 *
 * The guard only declines to call the handler. It never stops the event, so
 * anything else listening — a descendant's own handler included — still sees it.
 */
export function onModalDismiss(
  handler: (reason: ModalCloseReason) => void,
): (ev: MudCloseEvent) => void {
  return (ev) => {
    if (ev.target !== ev.currentTarget) return;

    const reason = ev.detail?.reason;
    if (typeof reason !== 'string') return;

    // Any string passes, rather than only today's four. A reason AGE adds
    // tomorrow must still close the modal: a guard that swallows a real
    // dismissal is worse than the bug it was written for.
    handler(reason as ModalCloseReason);
  };
}
